import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon, PaletteIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const BRAND = 'BILLEDKLAR™';
export const heroGuarantees = ['Øjeblikkelig digital adgang', 'Engangsbetaling', 'Intet abonnement', '7 dages tilfredshedsgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i de seks bind'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '12',
  label: 'Lektioner minut for minut i Bind 1'
}, {
  value: '5',
  label: 'Bonusser med i pakken'
}, {
  value: '3',
  label: 'Niveauer i elevarkene'
}];
export const differenceStats = [{
  value: '1.–6. klasse',
  label: 'Billedkunst fra indskoling til mellemtrin'
}, {
  value: '6 digitale bind',
  label: 'Fra tegning, maleri og ler til digitale billeder og kunsthistorie'
}, {
  value: 'PDF klar til print',
  label: 'Brug materialet digitalt eller print det til klassen'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Aftener og weekender forsvinder',
  text: 'Timevis foran skærmen med at planlægge næste uges billedkunst – og arbejdet er stadig langt fra færdigt.'
}, {
  title: 'Endeløs jagt på idéer',
  text: 'Idéer fra Pinterest og tilfældige hjemmesider passer sjældent til klassetrinnet, kræver dyre materialer eller mangler et fagligt mål.'
}, {
  title: 'Store forskelle i klassen',
  text: 'Nogle elever går i stå ved det blanke papir, andre er færdige på fem minutter – og timen skal fungere for alle.'
}, {
  title: 'Maling, ler og sakse overalt',
  text: 'Uden klare værkstedsrutiner og materialelister bliver dobbeltlektionen let til oprydning i stedet for billedfremstilling.'
}, {
  title: 'Evaluering og dokumentation tager tid',
  text: 'Kompetencemål efter 3. og 6. klassetrin, tegn på læring og elevplaner – hvordan vurderer du et billede retfærdigt?'
}, {
  title: 'Stress når timen begynder',
  text: 'Når tiden løber fra dig, bliver timen let til »tegn hvad I har lyst til« og improvisation i sidste øjeblik.'
}];
export const afterItems = ['Færdige årsplaner for 1.–2., 3.–4. og 5.–6. klasse', '12 færdige lektioner minut for minut', 'Mål, materialer og lærerens instruktion til hver lektion', 'Elevark i 3 niveauer', 'Billige materialer og faste værkstedsrutiner', 'Bygget på Fælles Mål for billedkunst', 'Billedfremstilling, billedanalyse og billedkommunikation', 'Med teknologiforståelse: digitale billeder og stop motion', 'Evalueringsskema og PDF-filer klar til print'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Aftenerne går med planlægning',
  text: 'Bruger du aftener og weekender på at planlægge næste uges billedkunst?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leder efter idéer, der virker',
  text: 'Leder du hele tiden på nettet efter opgaver, der er billige, gennemførlige og passer til klassetrinnet?'
}, {
  icon: UsersIcon,
  title: 'Store forskelle i klassen',
  text: 'Har du brug for elevark, der virker både for elever, der skal have støtte, og for elever, der skal udfordres?'
}, {
  icon: TargetIcon,
  title: 'Du underviser ikke kun i dit eget fag',
  text: 'Er du klasselærer og har fået billedkunst på skemaet uden at have faget som linjefag?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering tager tid',
  text: 'Vil du gerne have tjeklister til kompetencemålene, kriterier i 3 niveauer og feedback, du kan bruge med det samme?'
}, {
  icon: LayoutGridIcon,
  title: 'Du savner en klar struktur',
  text: 'Vil du gerne have alt samlet ét sted, så du hurtigt finder den rigtige lektion, når timen starter?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–6. klasse',
  title: 'Årsplan og 12 lektioner minut for minut',
  tagline: 'Du ved præcis, hvad der skal ske i hver time – hele skoleåret.',
  intro: 'Første bind giver dig færdige årsplaner for 1.–2., 3.–4. og 5.–6. klasse og tolv færdige lektioner, som du kan gennemføre med det samme.',
  items: ['Årsplaner for 1.–2., 3.–4. og 5.–6. klasse (40 uger)', '12-ugersplan og progressionstabel', 'Kompetencemål efter 3. og 6. klassetrin', 'Materialeliste og værkstedsrutiner', '12 dobbeltlektioner på 90 minutter, minut for minut', 'Mål, materialer og lærerens instruktion', 'Elevark i 3 niveauer til hver lektion', 'Farvecirklen som rød tråd', 'Evalueringsskema'],
  outro: 'Fra »Primærfarver og farveblanding« til »Plakat med budskab« – klar til klassen.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–6. klasse',
  title: 'Tegning, maleri og farver',
  tagline: 'Fra den første streg til det færdige maleri.',
  intro: 'Andet bind lærer eleverne at tegne, male og bruge farver bevidst – trin for trin og med elevark i 3 niveauer.',
  items: ['Tegneteknikker', 'Skygge og lys', 'Perspektiv', 'Farvecirklen', 'Akvarel og gouache', 'Landskab og portræt', 'Illustration', 'Elevark i 3 niveauer'],
  outro: 'Teknikker, eleverne kan bruge igen og igen.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '1.–6. klasse',
  title: 'Rumlige billeder og materialer',
  tagline: 'Ler, pap, tryk og genbrug – med styr på værkstedet.',
  intro: 'Tredje bind gør det rumlige konkret: skulptur, collage og tryk med enkle materialer og faste rutiner.',
  items: ['Ler', 'Papir og pap', 'Skulptur og installation', 'Collage', 'Tryk', 'Tekstil og genbrugsmaterialer', 'Værkstedsrutiner og oprydning', 'Sikkerhed med værktøj'],
  outro: 'Kreativ uro – med ro på.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '1.–6. klasse',
  title: 'Billedkommunikation og digitale billeder',
  tagline: 'Billeder, der fortæller noget – på papir og på skærm.',
  intro: 'Fjerde bind handler om plakater, symboler, tegneserier, foto og stop motion – og om at se kritisk på digitale billeder.',
  items: ['Plakat', 'Logo og symbol', 'Tegneserie', 'Foto', 'Stop motion', 'Digitale billeder og AI-billeder set kritisk', 'Teknologiforståelse i billedkunst', 'Billeder i medier og reklame'],
  outro: 'Eleverne lærer at skabe – og at gennemskue – billeder.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '1.–6. klasse',
  title: 'Billedanalyse og kunsthistorie',
  tagline: 'Fra Skagen til Kusama – kunst, eleverne kan tale om.',
  intro: 'Femte bind præsenterer danske kunstnere og verdenskunst og giver eleverne en fast metode til billedanalyse.',
  items: ['Skagensmalerne: P.S. Krøyer og Anna Ancher', 'Vilhelm Hammershøi', 'Asger Jorn og CoBrA', 'Olafur Eliasson', 'Verdenskunst: Van Gogh, Matisse, Kahlo, Kusama og Hokusai', 'Billedanalyse i 4 trin', 'Egne udtryk og identitet', 'Elevark i 3 niveauer'],
  outro: 'Kunsthistorie, der giver mening for eleverne.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '1.–6. klasse',
  title: 'Evaluering og projekter',
  tagline: 'Se, hvad eleverne kan – og vis det frem.',
  intro: 'Sjette bind samler tjeklister til kompetencemålene efter 3. og 6. klassetrin, kriterier i 3 niveauer og seks tværfaglige projekter.',
  items: ['Kompetencemål efter 3. og 6. klassetrin som tjeklister', 'Tegn på læring pr. kompetenceområde', 'Kriterier i 3 niveauer', 'Selv- og makkerevaluering', '6 tværfaglige projekter – inkl. klassens udstilling', '100 færdige feedbackkommentarer', 'Brobygning til valgfag billedkunst'],
  outro: 'Fuld støtte til en retfærdig evaluering – hele vejen til 6. klasse.',
  note: 'BILLEDKLAR™ er udviklet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i billedkunst',
  text: 'Et komplet undervisningssystem, der hjælper dig med at planlægge og gennemføre billedkunst hele skoleåret. Hvert bind har 40 sider med færdige lektioner, materialelister og elevark i 3 niveauer.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige lektioner minut for minut',
  text: 'Slut med det blanke papir. Klare planer til dobbeltlektionen på 90 minutter med mål, materialer og lærerens instruktion.'
}, {
  icon: FileTextIcon,
  title: 'Elevark i 3 niveauer',
  text: 'Samme mål i tre niveauer – støtte, basis og udfordring – så alle elever kan arbejde med den samme opgave.'
}, {
  icon: PaletteIcon,
  title: 'Billige materialer',
  text: 'Opgaver med maling, ler, papir og genbrug, materialelister og faste værkstedsrutiner – klar til billedkunstlokalet.'
}, {
  icon: LayersIcon,
  title: 'Bygget på Fælles Mål',
  text: 'Billedfremstilling, billedanalyse og billedkommunikation – med kompetencemål efter 3. og 6. klassetrin og teknologiforståelse.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering',
  text: 'Evalueringsskema, tegn på læring, tjeklister, kriterier i 3 niveauer og 100 færdige feedbackkommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan',
  text: 'Færdige årsplaner for 40 uger, så hele skoleåret i billedkunst hænger sammen.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [];
export const pageTypes = ['Færdige lektioner', 'Elevark i 3 niveauer', 'Årsplan', 'Materialelister', 'Evaluering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikarmappen til billedkunst',
  text: 'Lektioner med enkle materialer, som en vikar kan gennemføre uden forberedelse.',
  items: ['12 færdige lektioner', 'Instruktion til vikaren', 'Enkle materialer', 'Klar til print'],
  benefit: 'Ingen spildte timer, når du er fraværende.'
}, {
  title: 'Hurtigstart – 30 kreative opvarmninger',
  text: 'Korte opvarmninger på 5 minutter, der får eleverne i gang med at tegne og se fra første minut.',
  items: ['30 opvarmninger', '5 minutter hver', 'Tegne- og iagttagelsesøvelser', 'Kræver næsten ingen materialer'],
  benefit: 'Hver time starter med lyst til at skabe.'
}, {
  title: 'Visuel støtte i billedkunstlokalet',
  text: 'Kort, der gør rutiner og arbejdsgange synlige for alle elever.',
  items: ['Trinkort', 'Symbolkort', 'Materialekort', 'Oprydningskort'],
  benefit: 'Et lokale, hvor eleverne ved, hvad de skal.'
}, {
  title: 'Materialesmart – skab med lille budget',
  text: 'Idéer og lister, der får budgettet til at række længere.',
  items: ['Genbrug i billedkunst', 'Billige materialer', 'Færdig indkøbsliste', 'Tips til opbevaring'],
  benefit: 'Mere kunst for færre kroner.'
}, {
  title: 'Klassens kunstudstilling',
  text: 'Fra idé til fernisering – alt, du skal bruge for at vise elevernes billeder frem.',
  items: ['Skilte og værktekster', 'Invitationer til Aula', 'Kuratorroller til eleverne', 'Tjekliste til ferniseringen'],
  benefit: 'Elevernes arbejde bliver set.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Åbn BILLEDKLAR™',
  text: 'Hent pakken og åbn det bind, du skal bruge.'
}, {
  title: 'Vælg klasse og lektion',
  text: 'Find den lektion, den teknik eller det elevark, der passer til din klasse.'
}, {
  title: 'Forbered dig på 5 minutter',
  text: 'Læs lærerens instruktion og find materialerne frem – listen står på siden.'
}, {
  title: 'Print og start timen',
  text: 'Print elevarkene eller brug materialet digitalt – og gå ind til timen med en klar plan.'
}];
export const audienceItems = ['Du underviser i billedkunst i 1.–6. klasse', 'Du er klasselærer og har billedkunst på skemaet', 'Du har ikke billedkunst som linjefag', 'Du vil have en færdig årsplan bygget på Fælles Mål', 'Du vil have lektioner med billige materialer', 'Du vil have elevark i 3 niveauer', 'Du vil have hjælp til evaluering og kompetencemål', 'Du vil have dine aftener og weekender tilbage'];
export const timeValueTasks = ['At lede efter idéer på nettet', 'At planlægge lektioner', 'At lave elevark i flere niveauer', 'At skrive materialelister og købe ind', 'At evaluere og give feedback'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og lektioner, tegning og maleri, rumlige billeder, digitale billeder, billedanalyse og kunsthistorie, evaluering og projekter'
}, {
  label: '5 bonusser uden ekstra betaling',
  detail: 'Vikarmappen, Hurtigstart, Visuel støtte, Materialesmart og Klassens kunstudstilling'
}, {
  label: 'Til 1.–6. klasse',
  detail: 'Færdige lektioner, elevark i 3 niveauer, materialelister og evalueringsskema'
}];
export const offerIncludes = ['Øjeblikkelig digital adgang', 'PDF-filer du kan gemme', 'Klar til print', 'Intet fysisk produkt sendes'];
export const PRICE = '199 DKK';
export const PRICE_BEFORE = '399 DKK';

// Only add authentic reviews of BILLEDKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: 'BILLEDKLAR™ leveres digitalt. Efter købet får du adgang til PDF-filerne, som du kan hente og gemme. Du kan bruge materialet på computer, tablet eller mobil og printe de sider, du har brug for.'
}, {
  q: 'Følger materialet Fælles Mål?',
  a: 'Ja. BILLEDKLAR™ er bygget på Fælles Mål for billedkunst og de tre kompetenceområder Billedfremstilling, Billedanalyse og Billedkommunikation, med kompetencemål efter 3. og 6. klassetrin. BILLEDKLAR™ er udviklet uafhængigt og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.'
}, {
  q: 'Hvad med teknologiforståelse?',
  a: 'Teknologiforståelse er en del af billedkunst. BILLEDKLAR™ arbejder med digitale billeder, foto, stop motion og en kritisk tilgang til AI-billeder – især i Bind 4 og i lektionen om stop motion i Bind 1.'
}, {
  q: 'Hvilke klassetrin passer materialet til?',
  a: 'Materialet dækker 1.–6. klasse. Årsplanerne er delt op i 1.–2., 3.–4. og 5.–6. klasse, og hver lektion er markeret med klassetrin. Du vælger selv lektioner og elevark efter din klasse.'
}, {
  q: 'Hvilke materialer skal jeg bruge?',
  a: 'Lektionerne bruger almindelige og billige materialer som papir, blyanter, farver, maling, ler, sakse og genbrug. Hver lektion har en materialeliste, og bonussen Materialesmart hjælper dig med at få budgettet til at række.'
}, {
  q: 'Passer lektionerne til en dobbeltlektion?',
  a: 'Ja. Lektionerne i Bind 1 er planlagt minut for minut til en dobbeltlektion på 90 minutter, og de kan deles op i to lektioner på 45 minutter.'
}, {
  q: 'Kan jeg virkelig forberede en lektion på 5 minutter?',
  a: 'BILLEDKLAR™ er lavet til at gøre forberedelsen hurtig. Du åbner et bind, vælger en færdig lektion og læser lærerens instruktion igennem på cirka fem minutter. Tiden kan variere efter lektionen og de materialer, der skal findes frem.'
}, {
  q: 'Rækker materialet til hele skoleåret?',
  a: 'BILLEDKLAR™ indeholder årsplaner for 40 uger og færdige lektioner og forløb i alle bind. Du vælger, kombinerer og tilpasser materialet efter din egen plan og dine elevers behov.'
}, {
  q: 'Er der elevark i forskellige niveauer?',
  a: 'Ja. Elevarkene findes i 3 niveauer – støtte, basis og udfordring – så elever med forskellige behov kan arbejde mod det samme mål.'
}, {
  q: 'Er evaluering inkluderet?',
  a: 'Ja. Bind 1 har et evalueringsskema, og Bind 6 indeholder tegn på læring pr. kompetenceområde, tjeklister til kompetencemålene efter 3. og 6. klassetrin, kriterier i 3 niveauer, selv- og makkerevaluering og 100 færdige feedbackkommentarer.'
}, {
  q: 'Skal jeg printe det hele?',
  a: 'Nej. Materialet er PDF-filer. Du vælger selv, hvilke sider du printer, og du kan også bruge dele af materialet digitalt.'
}, {
  q: 'Er de fem bonusser inkluderet i prisen?',
  a: `Ja. Alle fem bonusser er med i pakken uden ekstra betaling – i alt ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Ingen løbende betalinger.'
}, {
  q: 'Er der en garanti?',
  a: 'Ja. BILLEDKLAR™ har 7 dages tilfredshedsgaranti. Er du mod forventning utilfreds med materialet, kan du kontakte os og få pengene tilbage inden for garantiperioden efter betingelserne.'
}, {
  q: 'Er det en officiel udgivelse?',
  a: 'Nej. BILLEDKLAR™ er et uafhængigt udviklet undervisningsmateriale bygget på Fælles Mål og er ikke en officiel udgivelse fra Børne- og Undervisningsministeriet.'
}];
export const footerLinks = ['Handelsbetingelser', 'Privatlivspolitik', 'Returnering', 'Kontakt'];
